import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { KN_BR_CUSTOMER_CURRENT } from 'src/constants/api.endpoints.constant';
import { Statuses } from 'src/feature-libs/kn-br-cart/saved-carts/root/models/kn-br-quote.model';
import { KnBrOrderHistoryService } from 'src/feature-libs/kn-br-order/core/facade/kn-br-order-history.service';
import { KnBrCommonService } from 'src/services/kn-br-common.service';
import { AtgOrderSearchSignalService } from 'src/services/atg-order-search-signal.service';
import { KnBrCustomerContextService } from 'src/store/kn-br-customer-context/kn-br-customer-context.service';
import { KnBrDateFormatService } from 'src/shared/kn-br-date-format.service';

export type OrderSearchTab = 'date' | 'order' | 'status';

type StatusKey = 'ALL_ORDERS' | 'O' | 'X' | 'S' | 'C' | 'past30Days' | 'past60Days' | 'past90Days';

const STATUS_VALUE_MAP: Partial<Record<StatusKey, string>> = {
  ALL_ORDERS: 'ALL_ORDERS',
  O: 'O',
  X: 'X',
  S: 'S',
  C: 'C',
};

const DAY_RANGE_MAP: Partial<Record<StatusKey, number>> = {
  past30Days: 30,
  past60Days: 60,
  past90Days: 90,
};

export interface OrderSearchModel {
  startDate: string;
  endDate: string;
  salesOrderNumber: string;
  purchaseOrderNumber: string;
  invoiceNumber: string;
  status: string;
}

@Component({
  selector: 'atg-homepage-my-orders',
  templateUrl: './atg-homepage-my-orders.component.html',
  styleUrls: ['./atg-homepage-my-orders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AtgHomepageMyOrdersComponent implements OnInit, OnDestroy {

  activeTab: OrderSearchTab = 'order';

  searchModel: OrderSearchModel = {
    startDate: '',
    endDate: '',
    salesOrderNumber: '',
    purchaseOrderNumber: '',
    invoiceNumber: '',
    status: '',
  };

  showPast90Days = false;

  startDateStruct: NgbDateStruct | null = null;
  endDateStruct: NgbDateStruct | null = null;

  statusSelection: Record<StatusKey, boolean> = {
    ALL_ORDERS: false,
    O: false,
    X: false,
    S: false,
    C: false,
    past30Days: false,
    past60Days: false,
    past90Days: false,
  };

  orderStatusList$: Observable<Statuses[]>;
  private customerContextSub?: Subscription;

  constructor(
    private router: Router,
    private atgOrderSearchSignalService: AtgOrderSearchSignalService,
    private knBrCommonService: KnBrCommonService,
    private knBrCustomerContextService: KnBrCustomerContextService,
    private knBrOrderHistoryService: KnBrOrderHistoryService,
    protected knBrDateFormatService: KnBrDateFormatService,
  ) {
    this.orderStatusList$ = this.knBrCommonService.loadOrderStatusList$();
  }

  ngOnInit(): void {
    this.customerContextSub = this.knBrCustomerContextService.get$.subscribe((value: any) => {
      const customerNumber = value?.customerNumber ?? KN_BR_CUSTOMER_CURRENT;
      this.knBrCommonService.getOrderStatusList(customerNumber);
    });
  }

  ngOnDestroy(): void {
    this.customerContextSub?.unsubscribe();
  }

  setTab(tab: OrderSearchTab): void {
    this.activeTab = tab;
  }

  onPast90DaysToggle(): void {
    if (this.showPast90Days) {
      this.setDateRange(90);
    } else {
      this.searchModel.startDate = '';
      this.searchModel.endDate = '';
      this.startDateStruct = null;
      this.endDateStruct = null;
    }
  }

  onStartDateChange(date: NgbDateStruct | null): void {
    this.searchModel.startDate = date ? this.structToISODate(date) : '';
  }

  onEndDateChange(date: NgbDateStruct | null): void {
    this.searchModel.endDate = date ? this.structToISODate(date) : '';
  }

  onStatusCheck(key: string): void {
    const k = key as StatusKey;
    (Object.keys(this.statusSelection) as StatusKey[]).forEach(sk => (this.statusSelection[sk] = false));
    this.statusSelection[k] = true;

    if (k in STATUS_VALUE_MAP) {
      this.searchModel.status = STATUS_VALUE_MAP[k] ?? '';
      this.searchModel.startDate = '';
      this.searchModel.endDate = '';
      this.startDateStruct = null;
      this.endDateStruct = null;
    } else if (k in DAY_RANGE_MAP) {
      this.searchModel.status = '';
      this.setDateRange(DAY_RANGE_MAP[k] ?? 0);
    }
  }

  private setDateRange(days: number): void {
    const today = new Date();
    const past = new Date();
    past.setDate(today.getDate() - days);
    this.searchModel.startDate = this.toISODate(past);
    this.searchModel.endDate = this.toISODate(today);
    this.startDateStruct = this.isoToStruct(this.searchModel.startDate);
    this.endDateStruct = this.isoToStruct(this.searchModel.endDate);
  }

  onSearch(): void {
    if (this.activeTab === 'order') {
      this.searchModel.salesOrderNumber = this.toUpperCaseTrim(this.searchModel.salesOrderNumber);
      this.searchModel.purchaseOrderNumber = this.toUpperCaseTrim(this.searchModel.purchaseOrderNumber);
      this.searchModel.invoiceNumber = this.toUpperCaseTrim(this.searchModel.invoiceNumber);

      this.knBrOrderHistoryService.searchOrders({
        salesOrderNumber: this.searchModel.salesOrderNumber,
        purchaseOrderNumber: this.searchModel.purchaseOrderNumber,
        invoiceNumber: this.searchModel.invoiceNumber,
      }).subscribe((preloadedResults: any) => {
        this.atgOrderSearchSignalService.set({
          tab: this.activeTab,
          searchModel: { ...this.searchModel },
          preloadedResults,
        });
        this.atgOrderSearchSignalService.setPreloadedOrderResults(preloadedResults);
        this.router.navigate(['/customer-360/orders']);
      });
      return;
    }

    this.knBrCustomerContextService.getActiveCustomerNumber().pipe(take(1)).subscribe((customerNumber) => {
      if (!customerNumber) {
        this.router.navigate(['/switch-account']);
        return;
      }

      this.atgOrderSearchSignalService.set({ tab: this.activeTab, searchModel: { ...this.searchModel } });
      this.router.navigate(['/customer-360/orders']);
    });
  }

  onReset(): void {
    this.showPast90Days = false;
    this.statusSelection = {
      ALL_ORDERS: false, O: false, X: false, S: false, C: false,
      past30Days: false, past60Days: false, past90Days: false,
    };
    this.searchModel = {
      startDate: '', endDate: '', salesOrderNumber: '', purchaseOrderNumber: '', invoiceNumber: '', status: '',
    };
    this.startDateStruct = null;
    this.endDateStruct = null;
  }

  private toUpperCaseTrim(value: string): string {
    return value ? value.trim().toUpperCase() : value;
  }

  private toISODate(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private structToISODate(struct: NgbDateStruct): string {
    const mm = String(struct.month).padStart(2, '0');
    const dd = String(struct.day).padStart(2, '0');
    return `${struct.year}-${mm}-${dd}`;
  }

  private isoToStruct(iso: string): NgbDateStruct | null {
    if (!iso) {
      return null;
    }
    const [year, month, day] = iso.split('-').map(Number);
    return { year, month, day };
  }

}
